import React, { useCallback, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { CurrentUser } from "../../business/helper/CurrentUser";
import { baseUrl } from "./MilaAiPage";
import Response from "../design/Chat/Response";
import stopIcon from "../../images/icons/stop.svg";
import milaIcon from "../../images/logo.svg";
import Button from "../design/Shared/Button";
import NounAwareTextArea from "./NounAwareTextArea";
import { useMilaContext } from "../context/useMilaContext";
import { useNounRecognition } from "../../hooks/useNounRecognition";
import { useMentionCompletion } from "../../hooks/useMentionCompletion";

const TestScheduledQuestion = ({ question, onBack, onScheduleNow, userData }) => {
  const [editedQuestion, setEditedQuestion] = useState(question);
  const [conversation, setConversation] = useState([]);
  const [charts, setCharts] = useState({});
  const [tables, setTables] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const connectionRef = useRef(null);
  const chartsRef = useRef({});
  const tablesRef = useRef({});
  const responseEndRef = useRef(null);
  const autoFiredRef = useRef(false);

  const TEST_THREAD_ID = "test-preview";

  // ── Noun recognition + @mention (same behaviour as MilaAiPage) ───────────
  const milaContext = useMilaContext();
  const nounUserId = userData?.userId ?? null;
  const nounOuId = milaContext?.rootOu?.rootOu ?? userData?.ouId ?? null;
  const rootOU = nounOuId;

  const [mentionQuery, setMentionQuery] = useState(null);
  const { suggestions: mentionSuggestions, isLoading: isMentionLoading } =
    useMentionCompletion(mentionQuery, nounOuId);

  const { detections, updateAfterReplace, updateAfterMention } =
    useNounRecognition(editedQuestion, nounUserId, nounOuId, !!mentionQuery);

  const handleNounReplace = useCallback((newText, replacedDetection, candidateText) => {
    updateAfterReplace(replacedDetection, candidateText, newText);
    setEditedQuestion(newText);
  }, [updateAfterReplace]);

  const handleMentionApply = useCallback((newText) => {
    updateAfterMention(newText);
    setMentionQuery(null);
    setEditedQuestion(newText);
  }, [updateAfterMention]);

  useEffect(() => { chartsRef.current = charts; }, [charts]);
  useEffect(() => { tablesRef.current = tables; }, [tables]);

  // Connect then auto-fire the test once connected
  useEffect(() => {
    const userToken = CurrentUser.GetToken();
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/milahub`, {
        accessTokenFactory: async () => userToken?.access_token,
      })
      .withAutomaticReconnect()
      .build();

    connectionRef.current = conn;

    conn.start()
      .then(() => {
        if (!autoFiredRef.current) {
          autoFiredRef.current = true;
          runTestWithConn(conn, question);
        }
      })
      .catch((e) => console.error("Test hub connection failed:", e));

    return () => { conn.stop(); };
  }, []);

  const registerHandlers = (conn, currentQuestion) => {
    conn.off("ReceiveMessage");

    conn.on("ReceiveMessage", (message) => {
      if (!message) return;
      const process = async () => {
        let response = {};
        try {
          response = JSON.parse(message);

          if (response.Table) {
            const idx = (tablesRef.current[TEST_THREAD_ID] || []).length;
            setTables((prev) => {
              const updated = { ...prev, [TEST_THREAD_ID]: [...(prev[TEST_THREAD_ID] || []), { ...response.Table }] };
              tablesRef.current = updated;
              return updated;
            });
            response.Text = (response.Text || "") + `\n\n{{TABLE:${idx}}}\n\n`;
            response.Table = null;
          }

          if (response.Chart) {
            const idx = (chartsRef.current[TEST_THREAD_ID] || []).length;
            setCharts((prev) => {
              const updated = { ...prev, [TEST_THREAD_ID]: [...(prev[TEST_THREAD_ID] || []), { ...response.Chart }] };
              chartsRef.current = updated;
              return updated;
            });
            response.Text = (response.Text || "") + `\n\n{{CHART:${idx}}}\n\n`;
            response.Chart = null;
          }

          if (response.IsProcessing === true) {
            setProcessingMessage(response.Message || "");
            response.Text = "";
          }
        } catch (e) {
          response.Text = "Something went wrong. Please try again.";
          response.IsEnded = true;
        }

        if (response.Text) {
          setConversation((prev) => {
            const last = prev[prev.length - 1];
            return [...prev.slice(0, -1), { ...last, response: last.response + response.Text }];
          });
        }

        if (responseEndRef.current) responseEndRef.current.scrollIntoView({ behavior: "smooth" });

        if (response.IsEnded === true) {
          setIsLoading(false);
          setIsDone(true);
          setProcessingMessage("");
        }
      };
      process();
    });
  };

  const ensureConnected = async (conn) => {
    if (conn.state === signalR.HubConnectionState.Connected) return;
    if (conn.state === signalR.HubConnectionState.Disconnected) {
      await conn.start();
      return;
    }
    // Connecting / Reconnecting — poll until ready
    await new Promise((resolve, reject) => {
      const maxWait = 8000;
      const interval = 100;
      let elapsed = 0;
      const timer = setInterval(() => {
        elapsed += interval;
        if (conn.state === signalR.HubConnectionState.Connected) {
          clearInterval(timer);
          resolve();
        } else if (elapsed >= maxWait) {
          clearInterval(timer);
          reject(new Error("Connection timeout"));
        }
      }, interval);
    });
  };

  const runTestWithConn = async (conn, q) => {
    setConversation([{ question: q, response: "" }]);
    setCharts({});
    setTables({});
    chartsRef.current = {};
    tablesRef.current = {};
    setIsLoading(true);
    setIsDone(false);
    setProcessingMessage("");

    registerHandlers(conn, q);

    try {
      await ensureConnected(conn);
      await conn.invoke("ProcessPromptTest", userData?.ouId, q, userData?.userId, "en-GB");
    } catch (e) {
      console.error("ProcessPromptTest error:", e);
      setIsLoading(false);
    }
  };

  const runTest = async () => {
    const conn = connectionRef.current;
    if (!conn) return;
    await runTestWithConn(conn, editedQuestion);
  };

  const handleStop = () => {
    connectionRef.current?.stop();
    setIsLoading(false);
    setIsDone(true);

    const userToken = CurrentUser.GetToken();
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/milahub`, { accessTokenFactory: async () => userToken?.access_token })
      .withAutomaticReconnect()
      .build();
    connectionRef.current = conn;
    conn.start().catch((e) => console.error("Reconnect failed:", e));
  };

  return (
    <div style={{ width: "min(90vw, 1100px)", height: "min(85vh, 700px)", display: "flex", flexDirection: "column" }}>

      {/* ── Header (fixed) ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-8 py-5 border-b border-whitesmoke-100">
        <span className="text-xl font-bold text-dark-blue-100">Test Scheduled Question</span>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
          className="hover:cursor-pointer flex-shrink-0" onClick={onBack}>
          <path d="M4 4L16 16M16 4L4 16" stroke="#303C54" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* ── Search field (fixed) ── */}
      <div className="flex-shrink-0 px-16 pt-6 pb-4">
        <div className="flex flex-col border border-[#F0E5FB] bg-white rounded-3xl overflow-hidden">
          <NounAwareTextArea
            rowsCount={2}
            value={editedQuestion}
            onChangeEvent={(e) => setEditedQuestion(e.target.value)}
            disabledState={isLoading}
            placeholderLabel="Type your question here"
            classNames="noun-textarea w-full px-6 pt-5 pb-2 text-sm border-0 focus:outline-none text-dark-blue-100 placeholder:text-[#A6ABB6] bg-transparent resize-none"
            detections={detections}
            onReplace={handleNounReplace}
            rootOU={rootOU}
            mentionSuggestions={mentionSuggestions}
            isMentionLoading={isMentionLoading}
            onMentionQueryChange={setMentionQuery}
            onMentionApply={handleMentionApply}
          />
          <div className="flex justify-end px-4 pb-3">
            <Button
              type="button"
              onClick={!isLoading ? runTest : handleStop}
              disabled={!isLoading && !editedQuestion.trim()}
              className="flex items-center justify-center w-[80px] h-[42px] gap-2 bg-dark-blue-100 hover:bg-dark-blue-hover text-[#C4C2F4] text-sl font-notosans font-bold leading-normal rounded-[18px] w-fit p-3 cursor-pointer"
            >
              <img src={!isLoading ? milaIcon : stopIcon} style={{ width: "18px" }} alt="run" />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Response area (scrollable) ── */}
      <div className="flex-1 overflow-y-auto px-16 py-2">
        {conversation.length > 0 && (
          <>
            <Response
              isLastItem={true}
              isLoading={isLoading}
              loadingExcel={false}
              favorites={[]}
              conv={conversation[0]}
              charts={charts}
              tables={tables}
              threadId={TEST_THREAD_ID}
              processingMessage={isLoading ? processingMessage : ""}
              hideActions={true}
            >
              {conversation[0].response}
            </Response>
            <div ref={responseEndRef} />
          </>
        )}
      </div>

      {/* ── Footer buttons (fixed) ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-16 py-5 border-t border-whitesmoke-100">
        <button
          onClick={onBack}
          className="rounded-full border border-dimgray-200 text-dark-blue-100 font-semibold text-sm py-3 px-8 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => onScheduleNow(editedQuestion)}
          disabled={!isDone}
          className="rounded-full border border-dark-blue-100 bg-dark-blue-100 text-white font-bold text-sm py-3 px-8 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          Schedule now
        </button>
      </div>

    </div>
  );
};

export default TestScheduledQuestion;
